import { noop } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from '@tanstack/react-router'

// The system runs on one face at five weights — 400/500 for prose and
// labels, 600/700 for figures and headings, 800 for display. No mono family:
// IBM Plex Sans's own tabular numerals carry the price columns.
import '@fontsource/ibm-plex-sans/400.css'
import '@fontsource/ibm-plex-sans/500.css'
import '@fontsource/ibm-plex-sans/600.css'
import '@fontsource/ibm-plex-sans/700.css'
import '@fontsource/noto-sans-devanagari/400.css'
import '@/styles/globals.css'

import Providers from '@/app/providers'
import { queryClient } from '@/app/query-client'
import { router } from '@/app/router'
import { watchForSignOut } from '@/app/session-sync'
import { useAuthStore } from '@/lib/auth/store'
import { dismissBootSplash } from '@/lib/boot-splash'
import { isEmbedded, openingTheme } from '@/lib/embed/bridge'
import { startEmbed } from '@/lib/embed/start'
import { initUi } from '@/lib/ui-store'

initUi({ appTheme: openingTheme() })

// Inside the Flutter app's WebView (lib/embed): tokens come from the app, and
// nothing leaves the chart's layout. Before the session check below, which is
// the first thing it asks the app for.
if (isEmbedded()) {
  startEmbed({
    history: router.history,
    staysOnChart: (pathname) =>
      router.getMatchedRoutes(pathname)[0].some((route) => route.id === '/_workspace'),
  })
}

// index.html's chart splash covers #root until a page is on screen; any route
// that resolves (a redirect to sign-in, a not-found) must not stay under it.
router.subscribe('onResolved', dismissBootSplash)

/**
 * Before render, deliberately. A reloaded tab's access token is back in memory
 * synchronously, before the first route is evaluated, so even a route no
 * guard waits on (/verify/$code) has it. A tab with none starts its one
 * cookie refresh here; the route guards await the same request rather than
 * starting their own (lib/auth/guards.ts).
 *
 * An access token that expired while the tab was closed does NOT bounce them:
 * the guard asks whether there is a session, not whether the token is still
 * good, and the api client refreshes an expired token before the first
 * request behind it ever goes out. The refresh cookie lives 7 days against the
 * access token's 30 minutes, so that is the ordinary case for a tab reopened
 * the next morning. Only a refresh that actually fails ends the session.
 *
 * Not awaited: it never rejects (a failed refresh settles as signed out), and
 * the router waits on it where it matters.
 */
void useAuthStore.getState().ensureSession()

/**
 * A chart tab (every chart opens in a tab of its own, docs/trading-chart.md)
 * asks for the chart page's code now, beside the refresh above. The router
 * asks for a route's code only once the layout's guard has the refresh's
 * answer, which put a round trip after it. A failure here is not lost: the
 * router asks again when it loads the route, and reports it there.
 */
if (/^\/chart(\/|$)/.test(window.location.pathname)) {
  for (const id of ['/_workspace', '/_workspace/chart/$symbol'] as const) {
    router.loadRouteChunk(router.routesById[id])?.catch(noop)
  }
}

const container = document.getElementById('root')
if (!container) throw new Error('Root element #root is missing from index.html')

// DEV ONLY: the Stage from a recorded answer, without signing in. Behind
// `import.meta.env.DEV` and a dynamic import, so a build carries none of it.
if (import.meta.env.DEV && window.location.pathname === '/dev/stage-preview') {
  dismissBootSplash()
  void import('@/dev/stagePreview').then(({ mountStagePreview }) => {
    mountStagePreview(container)
  })
} else {
  boot(container)
}

function boot(root: HTMLElement) {
  // See session-sync.ts for why this is a subscription. Its boot needs no
  // ordering against the line above: a tab that finds no session goes from
  // 'unknown' to 'anonymous', which the watcher ignores.
  watchForSignOut({
    queryClient,
    onSignedOut: () => {
      // The app's WebView has no sign-in of its own: the app signs the member
      // in, and the workspace layout shows the ended state until it does.
      if (isEmbedded()) return
      // `replace`: the screen they were signed out of is no longer theirs to
      // go back to — until they sign in again. A session that died under them
      // (a refresh refused, as after a long stay on a payment gateway) carries
      // the page, query string and all, so signing in lands them back on it:
      // `/payment/return` then confirms the payment it was sent back with. One
      // they ended themselves does not. /signin's search schema still refuses
      // any `redirect` that is not an app path.
      const expired = useAuthStore.getState().signedOutBy !== 'member'
      void router.navigate({
        to: '/signin',
        search: expired ? { redirect: router.state.location.href } : {},
        replace: true,
      })
    },
  })

  createRoot(root).render(
    <StrictMode>
      <Providers>
        <RouterProvider router={router} />
      </Providers>
    </StrictMode>,
  )
}
